import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Layers, Loader2, Plus, Search, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type AssignmentGroup, type Team } from '@/api/resourceCenterApi'
import { useAssignmentGroups, useInvalidateResourceProfiles, useTeams } from '@/hooks/useResourceCenter'
import { sameSet } from '@/lib/assignmentGroups'
import { CreateTeamDialog } from './CreateTeamDialog'

const messageOf = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.')

/**
 * Resources → Teams tab (z8nrz7f7dk). A team sits above groups: "Micro" holds
 * Micro-1, Micro-2 and Micro QC. A group may be in several teams, and people
 * are never put in a team directly — their teams are their groups' teams.
 */
export function TeamsTab({ canManage }: { canManage: boolean }) {
  const teams = useTeams()
  const groups = useAssignmentGroups()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const list = teams.data ?? []
  const selected = list.find(t => t.id === selectedId) ?? list[0] ?? null

  return (
    <div className='space-y-3'>
      {error && <p className='rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}
      {teams.error && <p className='text-sm text-destructive'>{teams.error.message}</p>}

      <div className='grid gap-4 lg:grid-cols-[18rem_1fr]'>
        <aside className='flex flex-col gap-3 rounded-2xl border bg-background p-3 shadow-sm lg:sticky lg:top-4 lg:self-start'>
          <div className='flex items-center justify-between px-1'>
            <h2 className='text-sm font-semibold'>
              Teams <span className='font-normal text-muted-foreground'>({list.length})</span>
            </h2>
            {canManage && (
              <Button size='sm' className='h-8 rounded-lg' onClick={() => setCreating(true)}>
                <Plus className='mr-1 h-4 w-4' /> New team
              </Button>
            )}
          </div>
          {teams.isLoading && <p className='px-1 text-sm text-muted-foreground'>Loading…</p>}
          {teams.isSuccess && list.length === 0 && (
            <div className='rounded-xl border border-dashed px-4 py-8 text-center'>
              <p className='text-sm font-medium'>No teams yet</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                A team gathers groups — Micro holds Micro-1, Micro-2 and Micro QC.
              </p>
            </div>
          )}
          <nav className='space-y-1'>
            {list.map(t => {
              const active = selected?.id === t.id
              return (
                <button
                  key={t.id}
                  type='button'
                  onClick={() => setSelectedId(t.id)}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'flex w-full flex-col gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-all',
                    active
                      ? 'border-primary/40 bg-primary/[0.06] shadow-sm'
                      : 'border-transparent hover:border-border hover:bg-muted/50',
                  )}
                >
                  <span className='truncate text-sm font-medium'>{t.name}</span>
                  <span className='flex gap-3 text-xs text-muted-foreground'>
                    <span className='flex items-center gap-1'>
                      <Layers className='h-3 w-3' /> {t.groups.length} {t.groups.length === 1 ? 'group' : 'groups'}
                    </span>
                    <span className='flex items-center gap-1'>
                      <Users className='h-3 w-3' /> {t.memberCount}
                    </span>
                  </span>
                </button>
              )
            })}
          </nav>
        </aside>

        <section className='min-w-0'>
          {selected ? (
            <TeamDetail
              key={selected.id}
              team={selected}
              groups={groups.data ?? []}
              canManage={canManage}
              onError={setError}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className='flex flex-col items-center gap-2 rounded-2xl border border-dashed bg-background px-6 py-20 text-center'>
              <Layers className='h-6 w-6 text-muted-foreground' />
              <p className='text-sm text-muted-foreground'>Create a team, then choose the groups in it.</p>
            </div>
          )}
        </section>
      </div>
      <CreateTeamDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={t => {
          setError(null)
          setSelectedId(t.id)
        }}
      />
    </div>
  )
}

function TeamDetail({
  team,
  groups,
  canManage,
  onError,
  onDeleted,
}: {
  team: Team
  groups: AssignmentGroup[]
  canManage: boolean
  onError: (message: string | null) => void
  onDeleted: () => void
}) {
  const invalidate = useInvalidateResourceProfiles()
  const [name, setName] = useState(team.name)
  const [description, setDescription] = useState(team.description ?? '')
  const saved = team.groups.map(g => g.id)
  const [picked, setPicked] = useState<string[]>(saved)
  const [search, setSearch] = useState('')

  // Adopt the server's copy after a save lands.
  useEffect(() => {
    setName(team.name)
    setDescription(team.description ?? '')
    setPicked(team.groups.map(g => g.id))
  }, [team])

  const done = () => {
    onError(null)
    void invalidate()
  }
  const fail = (e: unknown) => onError(messageOf(e))
  // The save mutation awaits updateTeam then setTeamGroups in sequence, so the
  // first can land on the server while the second throws. Without refetching,
  // the team query still holds the pre-save details and the next save attempt
  // re-diffs against stale data.
  const failPartialSave = (e: unknown) => {
    fail(e)
    void invalidate()
  }

  const detailsDirty = name.trim() !== team.name || (description.trim() || null) !== team.description
  const groupsDirty = !sameSet(picked, saved)

  const save = useMutation({
    mutationFn: async () => {
      if (detailsDirty) {
        await resourceProfilesApi.updateTeam(team.id, {
          name: name.trim(),
          description: description.trim() || null,
        })
      }
      if (groupsDirty) await resourceProfilesApi.setTeamGroups(team.id, picked)
    },
    onSuccess: done,
    onError: failPartialSave,
  })
  const remove = useMutation({
    mutationFn: () => resourceProfilesApi.deleteTeam(team.id),
    onSuccess: () => {
      done()
      onDeleted()
    },
    onError: fail,
  })

  const needle = search.trim().toLowerCase()
  const shown = needle ? groups.filter(g => g.name.toLowerCase().includes(needle)) : groups

  return (
    <div className='space-y-4 rounded-2xl border bg-background p-5 shadow-sm'>
      <div className='grid gap-3 sm:grid-cols-2'>
        <label className='space-y-1'>
          <span className='block text-xs font-medium text-muted-foreground'>Team name</span>
          <Input value={name} onChange={e => setName(e.target.value)} readOnly={!canManage} className='h-9' />
        </label>
        <label className='space-y-1'>
          <span className='block text-xs font-medium text-muted-foreground'>
            Description <span className='font-normal'>(optional)</span>
          </span>
          <Input
            value={description}
            onChange={e => setDescription(e.target.value)}
            readOnly={!canManage}
            className='h-9'
          />
        </label>
      </div>

      <div className='space-y-2'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h3 className='text-sm font-semibold'>
            Groups in this team{' '}
            <span className='font-normal text-muted-foreground'>
              ({picked.length} · {team.memberCount} {team.memberCount === 1 ? 'person' : 'people'})
            </span>
          </h3>
          {groups.length > 8 && (
            <div className='relative w-56'>
              <Search className='pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-muted-foreground' />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Find a group'
                aria-label='Find a group'
                className='h-8 pl-8 text-sm'
              />
            </div>
          )}
        </div>
        <p className='text-xs text-muted-foreground'>
          A group can be in more than one team. Everyone in a ticked group is in this team.
        </p>
        {groups.length === 0 ? (
          <p className='rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground'>
            No groups yet — create them on the Groups tab.
          </p>
        ) : (
          <ul className='grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3'>
            {shown.map(g => {
              const on = picked.includes(g.id)
              const others = g.teams.filter(t => t.id !== team.id)
              return (
                <li key={g.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors',
                      on ? 'border-primary/40 bg-primary/[0.04]' : 'hover:bg-muted/50',
                      !canManage && 'cursor-default',
                    )}
                  >
                    <Checkbox
                      checked={on}
                      disabled={!canManage}
                      onCheckedChange={v =>
                        setPicked(p => (v === true ? [...p, g.id] : p.filter(x => x !== g.id)))
                      }
                      aria-label={g.name}
                      className='mt-0.5'
                    />
                    <span className='min-w-0'>
                      <span className='block truncate text-sm font-medium'>{g.name}</span>
                      <span className='block text-xs text-muted-foreground'>
                        {g.members.length} {g.members.length === 1 ? 'person' : 'people'}
                        {others.length > 0 && ` · also in ${others.map(t => t.name).join(', ')}`}
                      </span>
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {canManage && (
        <div className='flex items-center justify-between gap-2 border-t pt-3'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='text-destructive'
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete the team "${team.name}"? Its groups and their people stay as they are.`)) {
                remove.mutate()
              }
            }}
          >
            <Trash2 className='mr-1.5 h-3.5 w-3.5' /> Delete team
          </Button>
          <Button
            type='button'
            size='sm'
            disabled={(!detailsDirty && !groupsDirty) || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Save changes
          </Button>
        </div>
      )}
    </div>
  )
}
